import { Alert, Badge, Button, Card, cn, Skeleton } from '@lastrouter/ui';
import { CheckCircle2, CircleAlert, CircleDashed, XCircle } from '@lastrouter/ui';
import { useBlocker } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client.js';
import { errorMessage } from '../api/hooks.js';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex max-w-3xl flex-col gap-1">
        <h1 className="text-[length:var(--lr-text-xl)] font-semibold leading-tight text-fg">{title}</h1>
        {subtitle ? <p className="text-fg-secondary">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** Erro com a mensagem para a pessoa, o código para o suporte e o botão de tentar de novo. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const correlation = error instanceof ApiError ? error.correlationId : null;
  const details = error instanceof ApiError ? error.details : [];
  return (
    <Alert tone="danger" title={t('common.loadError')}>
      <p>{errorMessage(t, error)}</p>
      {details.length ? (
        <ul className="mt-1 list-disc pl-5">
          {details.slice(0, 8).map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      ) : null}
      {correlation ? <p className="mt-1 font-mono text-[length:var(--lr-text-xs)]">{t('common.correlation', { id: correlation })}</p> : null}
      {onRetry ? (
        <Button size="sm" className="mt-2" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </Alert>
  );
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className="lr-skeleton-delay flex flex-col gap-2">
      <span className="sr-only">{t('common.loading')}</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

/** Mostra carregando, erro ou o conteúdo, para toda consulta ter os três estados escritos. */
export function QueryState<T>({
  query,
  children,
  rows,
}: {
  query: { isPending: boolean; error: unknown; data: T | undefined; refetch: () => unknown; isPlaceholderData?: boolean };
  children: (data: T) => ReactNode;
  rows?: number;
}) {
  if (query.isPending) return <LoadingBlock rows={rows ?? 4} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  // Filtro ou página nova: o conteúdo anterior fica esmaecido (e sem clique) até a resposta chegar, sem esqueleto.
  const stale = Boolean(query.isPlaceholderData);
  return (
    <div aria-busy={stale} className={cn('lr-fade contents', stale && 'lr-stale')}>
      {children(query.data as T)}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  // Fundo rebaixado, sem contorno tracejado: dentro de um Card não vira caixa dentro de caixa.
  return <p className="rounded-[var(--card-radius)] bg-sunken px-4 py-8 text-center text-fg-secondary">{children}</p>;
}

/** Indicador de painel: rótulo, valor grande e, opcionalmente, um detalhe. */
export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{label}</span>
      <span className="text-[length:var(--lr-text-2xl)] font-semibold leading-none text-fg">{value}</span>
      {hint ? <span className="text-[length:var(--lr-text-xs)] text-fg-muted">{hint}</span> : null}
    </Card>
  );
}

const STATUS_ICON = { success: CheckCircle2, danger: XCircle, warning: CircleAlert, neutral: CircleDashed, accent: CircleDashed } as const;

/** Estado sempre com ícone e texto: nunca só a cor. */
export function StatusBadge({ tone, children }: { tone: keyof typeof STATUS_ICON; children: ReactNode }) {
  const Icon = STATUS_ICON[tone];
  return (
    <Badge tone={tone}>
      <Icon aria-hidden="true" />
      {children}
    </Badge>
  );
}

export function SectionTitle({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h2 id={id} className="mb-3 text-[length:var(--lr-text-md)] font-semibold text-fg">
      {children}
    </h2>
  );
}

export function KeyValue({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[minmax(120px,max-content)_1fr] gap-x-6 gap-y-2">
      {items.map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-fg-secondary">{i.label}</dt>
          <dd className="min-w-0 break-words text-fg">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Pergunta antes de sair da tela (ou fechar a aba) com edição não salva. Trocar aba ou seção (só a busca muda) não pergunta. */
export function useUnsavedWarning(dirty: boolean) {
  const { t } = useTranslation();
  useBlocker({
    shouldBlockFn: ({ current, next }) => dirty && current.pathname !== next.pathname && !window.confirm(t('common.unsaved')),
    enableBeforeUnload: dirty,
  });
}

/** Link de texto: cor de destaque com 4,5:1 nos dois temas e foco visível. */
export const linkClass = 'rounded-[var(--lr-radius-sm)] text-accent-text underline underline-offset-4 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';
